export const ONBOARDING_CARD_IDS = ['background-image', 'invite-people', 'add-widget'] as const;

export type OnboardingCardId = (typeof ONBOARDING_CARD_IDS)[number];

// A 1:1 DM has no seat to fill, so its deck drops the invite card.
export const DM_ONBOARDING_CARD_IDS: readonly OnboardingCardId[] = ['background-image', 'add-widget'];

export const ONBOARDING_CARD_DISMISSED_EVENT = 'output:internal:onboarding-card-dismissed';

function dismissalKey(cardId: OnboardingCardId, workspaceId?: string | null, profileId?: string | null): string {
  // The background card keeps the standalone picker's original key: anyone who already
  // dismissed it before the stack existed shouldn't have it reappear as card one.
  const slug = cardId === 'background-image' ? 'workspace-background-picker' : `onboarding-card:${cardId}`;
  return `output:${slug}:dismissed:${workspaceId || 'unknown'}:${profileId || 'unknown'}`;
}

export function isOnboardingCardDismissed(cardId: OnboardingCardId, workspaceId?: string | null, profileId?: string | null): boolean {
  if (typeof window === 'undefined') return false;

  try {
    return window.localStorage.getItem(dismissalKey(cardId, workspaceId, profileId)) === 'true';
  } catch {
    return false;
  }
}

export function dismissOnboardingCard(cardId: OnboardingCardId, workspaceId?: string | null, profileId?: string | null): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(dismissalKey(cardId, workspaceId, profileId), 'true');
  } catch {
    // Dismissal still applies to the current render when storage is unavailable.
  }
  window.dispatchEvent(new CustomEvent(ONBOARDING_CARD_DISMISSED_EVENT, { detail: { cardId } }));
}

// The DM deck belongs to whoever was there first. There's no creator field on the DM,
// but only the creator can ever see it empty — the first real message is what reveals
// it to the peer — so seeing it empty IS the creator signal. The claim persists it
// (same per-device localStorage granularity as dismissals) so the creator's deck
// survives that first message instead of retiring with the empty state.
function deckClaimKey(workspaceId?: string | null, profileId?: string | null): string {
  return `output:onboarding-deck-claimed:${workspaceId || 'unknown'}:${profileId || 'unknown'}`;
}

export function claimOnboardingDeck(workspaceId?: string | null, profileId?: string | null): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(deckClaimKey(workspaceId, profileId), 'true');
  } catch {
    // The empty-DM check still shows the deck this session when storage is unavailable.
  }
}

export function hasOnboardingDeckClaim(workspaceId?: string | null, profileId?: string | null): boolean {
  if (typeof window === 'undefined') return false;

  try {
    return window.localStorage.getItem(deckClaimKey(workspaceId, profileId)) === 'true';
  } catch {
    return false;
  }
}

/** Card ids still worth showing, in deck order. */
export function remainingOnboardingCards(
  workspaceId?: string | null,
  profileId?: string | null,
  cardIds: readonly OnboardingCardId[] = ONBOARDING_CARD_IDS,
): OnboardingCardId[] {
  return cardIds.filter((cardId) => !isOnboardingCardDismissed(cardId, workspaceId, profileId));
}
